/**
 * MapSpec — the single versioned, declarative styling/render contract. It's the
 * whole artifact: the UI writes it, M5 (NL) writes the exact same shape (no
 * parallel representation), M6 persists it, and `render(PointSet, MapSpec)`
 * reproduces byte-identical output in editor / public URL / embed. Color
 * overrides and the deterministic value→color mapping live inside the spec so
 * the same category is the same color everywhere.
 */

import { z } from "zod";
import type { PointSet } from "./pointset";
import { AnalysisSpecSchema, migrateAnalysis } from "@/lib/analysis/spec";
import { OverlaySpecSchema, migrateOverlay } from "@/lib/overlay/spec";

export const MAPSPEC_VERSION = 1;

/** Auto-cluster once rendered points cross this legibility threshold. */
export const CLUSTER_LEGIBILITY_THRESHOLD = 400;
export const DEFAULT_CLUSTER_RADIUS = 50;
export const DEFAULT_CLUSTER_MAX_ZOOM = 14;

export const FilterOpSchema = z.enum([
  "is", "is_not", "in", "gte", "lte", "between", "contains", "empty", "not_empty",
]);
export type FilterOp = z.infer<typeof FilterOpSchema>;

export const FilterClauseSchema = z.object({
  column: z.string(),
  op: FilterOpSchema,
  value: z.union([z.string(), z.number(), z.array(z.string()), z.tuple([z.number(), z.number()]), z.null()]).optional(),
});
export type FilterClause = z.infer<typeof FilterClauseSchema>;

export const ColorBySchema = z.object({
  column: z.string(),
  mode: z.enum(["categorical", "numeric"]),
  /** value → hex override (categorical) so a user swatch change persists. */
  overrides: z.record(z.string(), z.string()).optional(),
});
export type ColorBy = z.infer<typeof ColorBySchema>;

export const SizeBySchema = z.object({
  column: z.string(),
  min: z.number().optional(),
  max: z.number().optional(),
});
export type SizeBy = z.infer<typeof SizeBySchema>;

/**
 * MAP-24 shape-by: marker shape varies by a column's category values, mirroring
 * `colorBy`. Just `{ column }` — the value→shape assignment is deterministic
 * (see `@/lib/map/shape`), so there is nothing per-value to persist (unlike
 * colorBy's swatch overrides). Absent → every marker keeps the single `marker`
 * shape (renders unchanged).
 */
export const IconBySchema = z.object({ column: z.string() });
export type IconBy = z.infer<typeof IconBySchema>;

/**
 * Marker shape/size (FAIL-476). Optional and additive: a spec with NO `marker`
 * renders as the historical small circle (back-compat — existing saved maps are
 * grandfathered). `defaultSpec` sets a readable teardrop pin, so only newly
 * created maps adopt the pin default. `pin`/`pin-solid` are the tintable
 * teardrops (SDF); `circle` is the classic dot. `size` composes with a `sizeBy`
 * encoding (data still scales relative to the chosen base).
 */
export const MARKER_SHAPES = [
  "pin",
  "pin-solid",
  "circle",
  "square",
  "diamond",
  "triangle",
  "star",
  "hexagon",
] as const;
export const MarkerShapeSchema = z.enum(MARKER_SHAPES);
export type MarkerShape = z.infer<typeof MarkerShapeSchema>;

export const MARKER_SIZES = ["s", "m", "l"] as const;
export const MarkerSizeSchema = z.enum(MARKER_SIZES);
export type MarkerSize = z.infer<typeof MarkerSizeSchema>;

export const MarkerSpecSchema = z.object({
  shape: MarkerShapeSchema,
  size: MarkerSizeSchema,
});
export type MarkerSpec = z.infer<typeof MarkerSpecSchema>;

/** The pin default new maps adopt (`defaultSpec`); absent `marker` stays circle. */
export const DEFAULT_MARKER: MarkerSpec = { shape: "pin", size: "m" };

export const MapSpecSchema = z.object({
  version: z.literal(MAPSPEC_VERSION),
  basemap: z.string(),
  theme: z.string().optional(),
  view: z.object({
    center: z.tuple([z.number(), z.number()]),
    zoom: z.number(),
    fit: z.boolean().optional(),
  }),
  encoding: z.object({
    colorBy: ColorBySchema.optional(),
    sizeBy: SizeBySchema.optional(),
    /** MAP-24 shape-by (mirrors colorBy). Absent → every marker keeps `marker.shape`. */
    iconBy: IconBySchema.optional(),
  }),
  /**
   * FAIL-476 marker shape/size. Optional: absent → the classic circle (existing
   * maps render unchanged); `defaultSpec` sets it to the pin default for new maps.
   */
  marker: MarkerSpecSchema.optional(),
  clustering: z.object({
    enabled: z.boolean(),
    radius: z.number(),
    maxZoom: z.number(),
  }),
  heat: z.object({
    enabled: z.boolean(),
    weightColumn: z.string().optional(),
  }),
  filters: z.array(FilterClauseSchema),
  popup: z.object({
    enabled: z.boolean(),
    titleColumn: z.string().optional(),
    fields: z.array(z.string()),
  }),
  legend: z.object({
    enabled: z.boolean(),
    collapsed: z.boolean(),
  }),
  /**
   * M8 deep-analysis sublayer — optional and independently versioned. Absent on
   * every M4 map (renders unchanged); present ones carry territories / radii /
   * (M8-B) drive-time / routes. Lives inside the spec so share/embed/NL all flow
   * through the one-spec contract. See @/lib/analysis/spec.
   */
  analysis: AnalysisSpecSchema.optional(),
  /**
   * M9 demographics/census overlay sublayer — optional and independently
   * versioned, exactly like `analysis`. Absent on every pre-M9 map (renders
   * unchanged); present ones carry the choropleth SELECTION (source / country /
   * variable / tier / classification / ramp token / opacity) that M4 render + M6
   * embed redraw deterministically from the overlay engine. See @/lib/overlay/spec.
   */
  overlay: OverlaySpecSchema.optional(),
});
export type MapSpec = z.infer<typeof MapSpecSchema>;

/** Validate an unknown value as a current-version MapSpec. */
export function parseMapSpec(input: unknown): MapSpec {
  return MapSpecSchema.parse(input);
}
export function safeParseMapSpec(input: unknown) {
  return MapSpecSchema.safeParse(input);
}

/**
 * Migration registry: keyed by the version being migrated FROM, each returns
 * the next-version shape. v1 is the current version (no-op). When bumping to
 * vN, add an entry `[N-1]: (spec) => ...` and update MAPSPEC_VERSION.
 */
type Migration = (spec: Record<string, unknown>) => Record<string, unknown>;
const MIGRATIONS: Record<number, Migration> = {
  // 1: (spec) => ({ ...spec, version: 2, /* new fields */ }),
};

/**
 * Migrate a spec of any supported prior version up to the current version, then
 * validate. A current-version spec passes straight through the validator.
 */
export function migrateMapSpec(input: unknown): MapSpec {
  if (typeof input !== "object" || input === null) {
    throw new Error("MapSpec must be an object");
  }
  let spec = input as Record<string, unknown>;
  let version = typeof spec.version === "number" ? spec.version : MAPSPEC_VERSION;
  if (version > MAPSPEC_VERSION) {
    throw new Error(`MapSpec version ${version} is newer than supported (${MAPSPEC_VERSION})`);
  }
  while (version < MAPSPEC_VERSION) {
    const migrate = MIGRATIONS[version];
    if (!migrate) throw new Error(`No migration from MapSpec version ${version}`);
    spec = migrate(spec);
    version = spec.version as number;
  }
  // The nested analysis block is versioned independently: migrate it up to the
  // current ANALYSIS_VERSION BEFORE the (strict, literal-pinned) MapSpec
  // validator runs, so a v(N-1) analysis block migrates cleanly under vN. A
  // spec with no analysis block is untouched and passes straight through.
  if (spec.analysis !== undefined && spec.analysis !== null) {
    spec = { ...spec, analysis: migrateAnalysis(spec.analysis) };
  }
  // The overlay block is versioned independently too — pre-migrate it up to the
  // current OVERLAY_VERSION before the strict validator runs. A spec with no
  // overlay block is untouched and passes straight through (renders unchanged).
  if (spec.overlay !== undefined && spec.overlay !== null) {
    spec = { ...spec, overlay: migrateOverlay(spec.overlay) };
  }
  return parseMapSpec(spec);
}

const NAME_LIKE = /name|title|label|company|business|store|place|customer|account/i;

/** Pick a category-like column for the default color-by (state/boolean, else a low-cardinality text column). */
function pickColorByColumn(pointSet: PointSet): PointSet["columns"][number] | null {
  const byType = pointSet.columns.find((c) => c.type === "state") ?? pointSet.columns.find((c) => c.type === "boolean");
  if (byType) return byType;
  return (
    pointSet.columns.find(
      (c) => c.type === "text" && c.distinctSample.length >= 2 && c.distinctSample.length <= 8 && !NAME_LIKE.test(c.name),
    ) ?? null
  );
}

function centerOf(pointSet: PointSet): [number, number] {
  const b = pointSet.bounds;
  if (!b) return [0, 0];
  return [(b.minLng + b.maxLng) / 2, (b.minLat + b.maxLat) / 2];
}

/**
 * Zero-config default spec (acceptance criterion 1): fit-to-bounds, auto color-by
 * a category-like column, auto-cluster past the legibility threshold, a legend,
 * and a sensible popup (name-like title + up to 3 fields). No user input.
 */
export function defaultSpec(pointSet: PointSet, opts?: { basemap?: string; theme?: string }): MapSpec {
  const colorCol = pickColorByColumn(pointSet);
  const titleCol = pointSet.columns.find((c) => NAME_LIKE.test(c.name)) ?? pointSet.columns[0];
  const fields = pointSet.columns
    .filter((c) => c.name !== titleCol?.name)
    .slice(0, 3)
    .map((c) => c.name);

  return {
    version: MAPSPEC_VERSION,
    basemap: opts?.basemap ?? "light",
    theme: opts?.theme,
    view: { center: centerOf(pointSet), zoom: 3, fit: true },
    encoding: colorCol ? { colorBy: { column: colorCol.name, mode: "categorical" } } : {},
    marker: DEFAULT_MARKER,
    clustering: {
      enabled: pointSet.count > CLUSTER_LEGIBILITY_THRESHOLD,
      radius: DEFAULT_CLUSTER_RADIUS,
      maxZoom: DEFAULT_CLUSTER_MAX_ZOOM,
    },
    heat: { enabled: false },
    filters: [],
    popup: { enabled: true, titleColumn: titleCol?.name, fields },
    legend: { enabled: true, collapsed: false },
  };
}
